// CreateNewSupplierInvoice.jsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import axios from '../../../../services/AxiosInstance';
import { getInboxItem } from '../../../../services/InboxService';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Alert, Card, Spinner, Button, Badge, Form, Row, Col } from 'react-bootstrap';
import FileDropZone from '../../SharedComponents/FileDropZone';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { aiOperationHeaders } from '../../../../services/AiOperation';
import { format } from 'date-fns';
import NewSupplierModal from '../Factoring/Suppliers/NewSupplierModal';
import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import swal from 'sweetalert';
import PageHeader from '../../../layouts/PageHeader';
import { toast } from 'sonner';

// Lucide React Icons
import {
  Upload,
  FileText,
  Building,
  Calendar,
  DollarSign,
  Check,
  AlertCircle,
  Info,
  X,
  Eye,
  Plus,
  Loader2,
  Brain,
  Camera,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  Edit3,
  Hash,
    ShoppingCart
} from 'lucide-react';

const CreateNewSupplierInvoice = () => {
  const { companyId, invoiceId } = useParams();
  const history = useHistory();

  // Edit mode detection
  const isEditMode = Boolean(invoiceId);

  // State variables - Enhanced for modern workflow
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date());
  const [dueDate, setDueDate] = useState(new Date());
  const [amount, setAmount] = useState('');
  // Betalningsreferens (OCR or free text) the supplier wants quoted; the
  // server decides the scheme (se_ocr when the digits pass modulus 10).
  const [paymentReference, setPaymentReference] = useState('');
  // Bank details the OCR read; sent with the create so the server can
  // PROPOSE a supplier payment method (never activate one).
  const [ocrPayee, setOcrPayee] = useState(null);
  const [status, setStatus] = useState('Scanned');
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showNewSupplierModal, setShowNewSupplierModal] = useState(false);
  const [initialSupplierData, setInitialSupplierData] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showInfo, setShowInfo] = useState(true);
  const [modalKey, setModalKey] = useState(0);
  const [loadingInvoice, setLoadingInvoice] = useState(isEditMode);

  // Modern workflow state. Start on the review step (2) not only in edit
  // mode but also when arriving from an inbox item — that flow already owns
  // the document, so showing the upload screen first would flash a blank
  // "drop a file" panel before the prefill effect switches it. Read the query
  // straight off window.location since useLocation() is set up further down.
  const startsFromInbox =
    !isEditMode && !!new URLSearchParams(window.location.search).get('from_inbox_item');
  const [currentStep, setCurrentStep] = useState(isEditMode || startsFromInbox ? 2 : 1);
  const [ocrData, setOcrData] = useState(null);
  const [fieldValidation, setFieldValidation] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [supplierSuggestions, setSupplierSuggestions] = useState([]);
  const [showSupplierSuggestions, setShowSupplierSuggestions] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [attachmentUrls, setAttachmentUrls] = useState({});
  const [fiscalYearWarning, setFiscalYearWarning] = useState(null);
  // Duplicate-invoice warning state. Populated by a debounced lookup
  // against POST /supplier-invoices/match-candidates whenever the user
  // has enough fields for the matcher to be meaningful. Suppressed by
  // `duplicateAcknowledged` after the user clicks "Spara ändå."
  const [duplicateCandidates, setDuplicateCandidates] = useState([]);
  const [duplicateAcknowledged, setDuplicateAcknowledged] = useState(false);

  // Phase 4a — "Skapa faktura från inkorg" deep-link prefill. When the
  // user arrives via `/.../create-supplier-invoices?from_inbox_item=<id>`
  // we fetch the inbox_item once and merge its `ocr_fields` into the
  // form state. The inbox_item id is kept in a ref so save handlers
  // below can include it in the create body — the backend then re-links
  // attachments and transitions the inbox_item atomically.
  const location = useLocation();
  const fromInboxItemIdRef = useRef(null);
  const inboxGenerationRef = useRef(null);
  // The File we pulled from the inbox attachment. Held by reference so the
  // save handler can tell "this is the inbox document, the backend re-links
  // it — don't re-upload" apart from a file the user dropped/swapped here.
  const inboxFileRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inboxItemId = params.get('from_inbox_item');
    if (!inboxItemId || isEditMode) return;
    fromInboxItemIdRef.current = inboxItemId;

    let cancelled = false;
    (async () => {
      try {
        const item = await getInboxItem(companyId, inboxItemId);
        inboxGenerationRef.current=Number(item.document_generation || 0);
        if (cancelled) return;

        // Skip the upload step — the inbox item already owns the document.
        // The user should land straight on the review form with the exact
        // PDF they were looking at, never a blank "drop a file" screen.
        setCurrentStep(2);

        // Pull the inbox attachment into the form as a File so the existing
        // files[0]/previewUrl viewer renders it. We DON'T re-upload it on
        // save — the backend re-links the original inbox attachment via
        // from_inbox_item_id (re-uploading the in-memory copy would
        // duplicate it). Multi-attachment items still carry all of them over
        // server-side; we preview/scan the first (the "primary" document).
        const firstAttachmentId = Array.isArray(item?.attachment_ids) ? item.attachment_ids[0] : null;
        let inboxFile = null;
        if (firstAttachmentId) {
          try {
            const resp = await axios.get(
              getApiUrl(`${companyId}/attachments/${firstAttachmentId}`),
              { withCredentials: true, responseType: 'blob' },
            );
            if (cancelled) return;
            const type = resp.headers['content-type'] || resp.data.type || 'application/pdf';
            const ext = type.includes('pdf') ? 'pdf' : (type.startsWith('image/') ? type.split('/')[1] : 'pdf');
            inboxFile = new File([resp.data], `bilaga.${ext}`, { type });
            inboxFileRef.current = inboxFile;
          } catch (e) {
            console.warn('[CreateSupplierInvoice] inbox attachment load failed:', e.message);
          }
        }

        const f = item?.ocr_fields;
        const hasOcr = !!f && Object.keys(f).length > 0;

        if (hasOcr) {
          // Already scanned in the inbox → reuse those fields. Re-running OCR
          // here would double-spend credits, so we only show the document.
          if (inboxFile) setFiles([inboxFile]);
          if (f.invoiceNumber) setInvoiceNumber(f.invoiceNumber);
          if (f.invoiceDate)   { const d = new Date(f.invoiceDate); if (!Number.isNaN(d.getTime())) setInvoiceDate(d); }
          if (f.dueDate)       { const d = new Date(f.dueDate);     if (!Number.isNaN(d.getTime())) setDueDate(d); }
          if (f.totalAmount != null) setAmount(String(f.totalAmount));
          if (f.ocrReference) setPaymentReference(String(f.ocrReference));
          if (f.bankgiro || f.iban) setOcrPayee({ bankgiro: f.bankgiro || null, iban: f.iban || null });
          // Surface the OCR'd supplier hint for the new-supplier modal.
          setInitialSupplierData({
            name: f.supplierName || '',
            orgnumber: f.supplierOrgNumber || '',
            address: f.supplierAddress || '',
            bankgiro: f.bankgiro || '',
            iban: f.iban || '',
          });
        } else if (inboxFile && !item?.ai_skipped_reason) {
          // Never scanned (a fresh `new`/`error` item) and OCR wasn't
          // deliberately skipped → run the scan now, exactly like a manual
          // upload: handleFilesUploaded sets `files`, calls analyze-invoice
          // (credit-gated server-side) and prefills + matches the supplier.
          await handleFilesUploaded([inboxFile]);
        } else if (inboxFile) {
          // OCR was skipped upstream (no credits / opt-out / feature off) —
          // show the document but don't auto-scan (it would just fail or
          // contradict the opt-out); the user fills fields in or tops up.
          setFiles([inboxFile]);
        }
      } catch (err) {
        console.warn('[CreateSupplierInvoice] prefill from inbox failed:', err.message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, companyId]);

  // Duplicate-invoice lookup. Fires when the user has enough fields for
  // the matcher to do something useful (supplier + either invoice_number
  // OR amount+date). Debounced 400ms so typing doesn't spam the
  // endpoint. Free on the backend (pure DB query) but no need to be
  // wasteful. Skipped on edit mode — editing the EXISTING invoice
  // would always match itself.
  useEffect(() => {
    if (isEditMode) return;
    if (duplicateAcknowledged) return;
    const supplier = suppliers.find((s) => s.supplier_id === selectedSupplier);
    if (!supplier) { setDuplicateCandidates([]); return; }
    const hasNumber = !!invoiceNumber?.trim();
    const hasAmount = !!amount && !Number.isNaN(parseFloat(amount));
    const hasDate   = isValidDate(invoiceDate);
    if (!hasNumber && !(hasAmount && hasDate)) {
      setDuplicateCandidates([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const url = getApiUrl(`${companyId}/purchasing/supplier-invoices/match-candidates`);
        const body = {
          supplierOrgNumber: supplier.orgnumber || null,
          supplierName:      supplier.name || null,
          invoiceNumber:     invoiceNumber?.trim() || null,
          totalAmount:       hasAmount ? parseFloat(amount) : null,
          invoiceDate:       hasDate ? safeFormatDate(invoiceDate, 'yyyy-MM-dd') : null,
        };
        const res = await axios.post(url, body, { withCredentials: true });
        setDuplicateCandidates(res.data?.candidates || []);
      } catch (err) {
        console.warn('[CreateSupplierInvoice] match-candidates lookup failed:', err.message);
      }
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSupplier, invoiceNumber, amount, invoiceDate, isEditMode, duplicateAcknowledged]);

  // Modern helper functions
  const getStepStatus = (step) => {
    if (step < currentStep) return 'completed';
    if (step === currentStep) return 'current';
    return 'pending';
  };

  // Helper to check if a date is valid
  const isValidDate = (date) => {
    if (!date) return false;
    const d = date instanceof Date ? date : new Date(date);
    return d instanceof Date && !isNaN(d.getTime());
  };

  // Helper to safely format a date
  const safeFormatDate = (date, formatStr = 'yyyy-MM-dd') => {
    if (!isValidDate(date)) return '';
    try {
      return format(date instanceof Date ? date : new Date(date), formatStr);
    } catch {
      return '';
    }
  };

  // Validate invoice date against fiscal years
  const validateFiscalYearDate = useCallback(async (date) => {
    if (!isValidDate(date)) {
      setFiscalYearWarning(null);
      return;
    }
    try {
      const formattedDate = format(date instanceof Date ? date : new Date(date), 'yyyy-MM-dd');
      const url = getApiUrl(`${companyId}/accounting/fiscal-years/validate-date?date=${formattedDate}`);
      const response = await axios.get(url, { withCredentials: true });
      setFiscalYearWarning(response.data.warning || null);
    } catch (err) {
      console.error('Error validating fiscal year date:', err);
      setFiscalYearWarning(null);
    }
  }, [companyId]);

  // Validate fiscal year when invoiceDate changes
  useEffect(() => {
    if (isValidDate(invoiceDate)) {
      const timeout = setTimeout(() => {
        validateFiscalYearDate(invoiceDate);
      }, 500);
      return () => clearTimeout(timeout);
    } else {
      setFiscalYearWarning(null);
    }
  }, [invoiceDate, validateFiscalYearDate]);

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'var(--bs-success)'; // Green
    if (confidence >= 0.6) return '#d97706'; // Orange
    return '#dc2626'; // Red
  };

  const validateField = useCallback((fieldName, value, confidence = 1) => {
    const validation = { isValid: true, message: '', confidence };

    switch (fieldName) {
      case 'invoiceNumber':
        if (!value || value.trim() === '') {
          validation.isValid = false;
          validation.message = 'Fakturanummer krävs';
        }
        break;
      case 'amount':
        if (!value || isNaN(parseFloat(value)) || parseFloat(value) <= 0) {
          validation.isValid = false;
          validation.message = 'Giltigt belopp krävs';
        }
        break;
      case 'supplier':
        if (!value || value === '') {
          validation.isValid = false;
          validation.message = 'Leverantör måste väljas';
        }
        break;
      case 'invoiceDate':
        if (!isValidDate(value)) {
          validation.isValid = false;
          validation.message = 'Giltigt fakturadatum krävs';
        }
        break;
      case 'dueDate':
        if (!isValidDate(value)) {
          validation.isValid = false;
          validation.message = 'Giltigt förfallodatum krävs';
        }
        break;
      default:
        break;
    }

    setFieldValidation(prev => ({
      ...prev,
      [fieldName]: validation
    }));

    return validation;
  }, []);

  // Re-validate supplier field when selection changes
  useEffect(() => {
    if (currentStep === 2 && selectedSupplier) {
      validateField('supplier', selectedSupplier);
    }
  }, [selectedSupplier, currentStep, validateField]);

  // Check if all required fields are valid to proceed to step 3
  const canProceedToStep3 = () => {
    const hasSupplier = selectedSupplier && selectedSupplier !== '';
    const hasInvoiceNumber = invoiceNumber && invoiceNumber.trim() !== '';
    const hasValidAmount = amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0;
    const hasValidInvoiceDate = isValidDate(invoiceDate);
    const hasValidDueDate = isValidDate(dueDate);

    return hasSupplier && hasInvoiceNumber && hasValidAmount && hasValidInvoiceDate && hasValidDueDate;
  };

  // Validate all fields and show errors
  const validateAllFields = () => {
    const supplierValid = validateField('supplier', selectedSupplier);
    const invoiceNumberValid = validateField('invoiceNumber', invoiceNumber);
    const amountValid = validateField('amount', amount);
    const invoiceDateValid = validateField('invoiceDate', invoiceDate);
    const dueDateValid = validateField('dueDate', dueDate);

    return supplierValid.isValid && invoiceNumberValid.isValid && amountValid.isValid &&
      invoiceDateValid.isValid && dueDateValid.isValid;
  };

  const handleClose = () => {
    setShowNewSupplierModal(false);
    // We don't immediately reset initialSupplierData here to allow the modal to clean up
  };

  const handleSave = (newSupplierData) => {
    saveNewSupplier(newSupplierData);
    setShowNewSupplierModal(false);
  };

  const openNewSupplierModal = (initialData = null) => {
    console.log('Opening new supplier modal with initial data:', initialData);
    setInitialSupplierData(initialData);
    setModalKey(prevKey => prevKey + 1); // Increment key to force re-render
    setShowNewSupplierModal(true);
  };

  const saveNewSupplier = (newData) => {
    const newSupplier = {
      name: newData.name,
      orgnumber: newData.orgnumber, // Updated to match the correct field name
      vatnumber: newData.vatnumber, // Updated to match the correct field name
      postadress1: newData.address,
      postadress2: '',
      city: newData.city,
      postnumber: newData.postalcode, // Updated to match the correct field name
      country: newData.country,
      phonenumber: newData.phonenumber, // Updated to match the correct field name
      // TIC register fields (set when the supplier was picked via lookup)
      tic_company_id: newData.tic_company_id || null,
      legal_entity_type: newData.legal_entity_type || null,
      f_tax_registered: newData.f_tax_registered ?? null,
      vat_registered: newData.vat_registered ?? null,
      sni_code: newData.sni_code || null,
    };

    console.log('New supplier data:', newSupplier);

    setIsProcessing(true);
    const createSupplierApiUrl = getApiUrl(`${companyId}/purchasing/suppliers`);
    axios
      .post(createSupplierApiUrl, newSupplier, { withCredentials: true })
      .then((response) => {
        if (response.status === 201) {
          toast.success('Leverantören har skapats.');
          fetchSuppliers().then(() => {
            // Set the newly created supplier as selected after fetch
            if (response.data && response.data.supplier_id) {
              setSelectedSupplier(response.data.supplier_id);
            } else {
              // If supplier_id is not in response, try to find it by name
              const newlyAddedSupplier = suppliers.find(s => s.name === newData.name);
              if (newlyAddedSupplier) {
                setSelectedSupplier(newlyAddedSupplier.supplier_id);
              }
            }
            setError(null);
          });
        } else {
          setError('Kunde inte lägga till leverantören. Vänligen försök igen.');
        }
      })
      .catch((error) => {
        console.error('Error saving supplier:', error);
        setError('Kunde inte lägga till leverantören. Vänligen försök igen.');
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  // Fetch existing invoice data if in edit mode
  const fetchInvoiceData = async () => {
    if (!isEditMode) return;

    try {
      setLoadingInvoice(true);
      const getInvoiceApiUrl = getApiUrl(`${companyId}/purchasing/supplier-invoices/${invoiceId}`);
      const response = await axios.get(getInvoiceApiUrl, { withCredentials: true });
      const invoiceData = response.data;

      // Populate form with existing data
      setSelectedSupplier(invoiceData.supplier_id || '');
      setInvoiceNumber(invoiceData.invoice_number || '');

      // Validate dates before setting
      const parsedInvoiceDate = invoiceData.invoice_date ? new Date(invoiceData.invoice_date) : null;
      const parsedDueDate = invoiceData.due_date ? new Date(invoiceData.due_date) : null;

      setInvoiceDate(isValidDate(parsedInvoiceDate) ? parsedInvoiceDate : new Date());
      setDueDate(isValidDate(parsedDueDate) ? parsedDueDate : new Date());

      setAmount(invoiceData.amount?.toString() || '');
      setPaymentReference(invoiceData.payment_reference || '');
      setStatus(invoiceData.status || 'Scanned');

      // Set attachments from invoice data (backend now includes them)
      if (invoiceData.attachments && Array.isArray(invoiceData.attachments)) {
        setExistingAttachments(invoiceData.attachments);
      }

      setLoadingInvoice(false);
    } catch (err) {
      console.error('Error fetching invoice data:', err);
      setError('Kunde inte hämta fakturadata. Vänligen försök igen senare.');
      setLoadingInvoice(false);
    }
  };

  useEffect(() => {
    fetchSuppliers().then(() => {
      // Fetch invoice data after suppliers are loaded (needed for validation)
      if (isEditMode) {
        fetchInvoiceData();
      }
    });
  }, []);

  // Function to fetch an attachment and create a Blob URL
  const fetchAttachmentBlob = async (attachmentId) => {
    const attachmentApiUrl = getApiUrl(`${companyId}/attachments/${attachmentId}`);
    const response = await axios.get(attachmentApiUrl, {
      withCredentials: true,
      responseType: 'blob',
    });
    const blob = new Blob([response.data], {
      type: response.headers['content-type'],
    });
    return URL.createObjectURL(blob);
  };

  // Fetch attachment Blob URLs when existingAttachments changes
  useEffect(() => {
    const fetchAllAttachments = async () => {
      if (existingAttachments && existingAttachments.length > 0) {
        const urls = {};
        for (const attachment of existingAttachments) {
          if (attachment.attachment_id) {
            try {
              const blobUrl = await fetchAttachmentBlob(attachment.attachment_id);
              urls[attachment.attachment_id] = blobUrl;
            } catch (error) {
              console.error('Error fetching attachment:', error);
            }
          }
        }
        setAttachmentUrls(urls);
      }
    };
    fetchAllAttachments();
  }, [existingAttachments, companyId]);

  // Cleanup Blob URLs when component unmounts
  useEffect(() => {
    return () => {
      Object.values(attachmentUrls).forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, [attachmentUrls]);

  // Fetch suppliers from the backend
  const fetchSuppliers = async () => {
    try {
      const getSuppliersApiUrl = getApiUrl(`${companyId}/purchasing/suppliers`);
      const result = await axios.get(getSuppliersApiUrl, { withCredentials: true });
      setSuppliers(result.data);
      return result.data;
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError('Kunde inte hämta leverantörer. Vänligen försök igen senare.');
      return [];
    }
  };

  // Handle files uploaded via FileDropZone
  const handleFilesUploaded = async (uploadedFiles) => {
    if (!uploadedFiles || uploadedFiles.length === 0) {
      return;
    }

    setIsProcessing(true);
    setError(null);
    setFiles(uploadedFiles); // Store the files for later use

    try {
      const file = uploadedFiles[0];

      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        getApiUrl(`${companyId}/purchasing/supplier-invoices/analyze-invoice`),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...aiOperationHeaders(),
          },
          withCredentials: true,
        }
      );

      const extractedData = response.data;
      console.log('🔍 DEBUG - Extracted OCR data:', extractedData);

      // Store OCR data for confidence display
      setOcrData(extractedData);

      // Update form fields with extracted data and validate
      if (extractedData.invoiceId && extractedData.invoiceId.value) {
        setInvoiceNumber(extractedData.invoiceId.value);
        validateField('invoiceNumber', extractedData.invoiceId.value, extractedData.invoiceId.confidence);
      }
      {
        const ext = extractedData.extended || {};
        if (ext.ocrNumber?.value) setPaymentReference(String(ext.ocrNumber.value));
        if (ext.sellerBankgiro?.value || ext.sellerIban?.value) setOcrPayee({ bankgiro: ext.sellerBankgiro?.value || null, iban: ext.sellerIban?.value || null });
      }

      if (extractedData.invoiceDate && extractedData.invoiceDate.value) {
        const parsedInvoiceDate = new Date(extractedData.invoiceDate.value);
        if (isValidDate(parsedInvoiceDate)) {
          setInvoiceDate(parsedInvoiceDate);
        } else {
          // Keep the default date if OCR returned an invalid date
          console.warn('OCR returned invalid invoice date:', extractedData.invoiceDate.value);
        }
      }

      if (extractedData.dueDate && extractedData.dueDate.value) {
        const parsedDueDate = new Date(extractedData.dueDate.value);
        if (isValidDate(parsedDueDate)) {
          setDueDate(parsedDueDate);
        } else {
          // Keep the default date if OCR returned an invalid date
          console.warn('OCR returned invalid due date:', extractedData.dueDate.value);
        }
      }

      if (extractedData.invoiceTotal && extractedData.invoiceTotal.amount) {
        setAmount(extractedData.invoiceTotal.amount);
        validateField('amount', extractedData.invoiceTotal.amount, extractedData.invoiceTotal.confidence);
      }

      // Match supplier by name or org number
      if (extractedData.vendorName && extractedData.vendorName.value) {
        const vendorName = extractedData.vendorName.value.toLowerCase();

        // Helper to normalize org numbers for comparison (strip spaces, dashes)
        const normalizeOrgNr = (val) => val ? val.replace(/[\s\-]/g, '') : '';

        // Extract org number from OCR data
        const extractedOrgNr = normalizeOrgNr(
          extractedData.extended?.sellerOrganizationNumber?.value || ''
        );

        // 1. Try exact name match
        let matchingSupplier = suppliers.find(
          (supplier) => supplier.name.toLowerCase() === vendorName
        );

        // 2. Try org number match (if we have one from OCR)
        if (!matchingSupplier && extractedOrgNr) {
          matchingSupplier = suppliers.find(
            (supplier) => normalizeOrgNr(supplier.orgnumber) === extractedOrgNr
          );
        }

        // 3. Try fuzzy name match (name contains or is contained)
        if (!matchingSupplier) {
          matchingSupplier = suppliers.find(
            (supplier) =>
              supplier.name.toLowerCase().includes(vendorName) ||
              vendorName.includes(supplier.name.toLowerCase())
          );
        }

        if (matchingSupplier) {
          setSelectedSupplier(matchingSupplier.supplier_id);
          validateField('supplier', matchingSupplier.supplier_id, 1.0);
        } else {
          setError(`Leverantören "${extractedData.vendorName.value}" hittades inte. Du kan lägga till en ny leverantör eller välja en befintlig.`);

          // Parse address parts from the combined vendor address
          const ext = extractedData.extended || {};
          const addressText = extractedData.vendorAddress?.text || '';
          const addressParts = addressText.split(',').map(s => s.trim());

          // Prepare initial supplier data for the modal using extended fields
          const initialData = {
            name: extractedData.vendorName.value || '',
            orgnumber: ext.sellerOrganizationNumber?.value || '',
            vatnumber: extractedData.vendorTaxId?.value || ext.sellerVatRegistrationNumber?.value || '',
            address: addressParts[0] || '',
            postalcode: addressParts[1]?.replace(/[^\d\s]/g, '').trim() || '',
            city: addressParts[1]?.replace(/[\d\s]/g, '').trim() || addressParts[1] || '',
            country: 'SE',
            phonenumber: ext.sellerPhone?.value || '',
          };

          // Set the initial data for the modal
          setInitialSupplierData(initialData);

          // Show supplier suggestions
          setSupplierSearchTerm(extractedData.vendorName.value || '');
          const suggestions = suppliers.filter(s =>
            s.name.toLowerCase().includes(vendorName.substring(0, 3))
          ).slice(0, 3);
          setSupplierSuggestions(suggestions);
        }
      }

      // Move to review step after successful OCR
      setCurrentStep(2);
      setError(null);

      console.log('✅ OCR processing completed, moved to review step');
    } catch (err) {
      console.error('Error uploading and processing invoice:', err);

      // Handle specific API errors
      if (err.response) {
        const { status, data } = err.response;

        // Handle insufficient AI credits (429)
        if (status === 429 && data?.error === 'INSUFFICIENT_CREDITS') {
          setError(null); // Clear generic error
          swal({
            title: 'AI-krediter slut',
            text: `Du har ${data.balance || 0} krediter kvar men behöver ${data.required || 5} för att analysera fakturan. Du kan köpa fler krediter eller fylla i fakturan manuellt.`,
            icon: 'warning',
            buttons: {
              cancel: 'Avbryt',
              manual: {
                text: 'Fyll i manuellt',
                value: 'manual',
              },
              confirm: {
                text: 'Köp krediter',
                value: 'buy',
              }
            }
          }).then((value) => {
            if (value === 'buy') {
              history.push(`/${companyId}/settings/subscription`);
            } else if (value === 'manual') {
              setCurrentStep(2);
            }
          });
          return;
        }

        // Handle rate limit exceeded (429)
        if (status === 429 && data?.error === 'RATE_LIMIT_EXCEEDED') {
          const message = data.reason === 'RATE_LIMIT_HOURLY'
            ? `Du har nått gränsen på ${data.limit} analyser per timme. Vänta en stund och försök igen.`
            : `Du har nått den dagliga gränsen på ${data.limit} analyser. Försök igen imorgon.`;
          setError(message);
          return;
        }

        // Handle feature not available (403)
        if (status === 403 && data?.error === 'FEATURE_NOT_AVAILABLE') {
          setError(null);
          swal({
            title: 'Funktion ej tillgänglig',
            text: data.message || 'Denna funktion är inte tillgänglig i din nuvarande prenumerationsplan.',
            icon: 'info',
            buttons: {
              cancel: 'Stäng',
              confirm: {
                text: 'Uppgradera',
                value: true,
              }
            }
          }).then((willUpgrade) => {
            if (willUpgrade) {
              history.push(`/${companyId}/settings/subscription#planer`);
            }
          });
          return;
        }

        // Handle subscription cancelled (403)
        if (status === 403 && data?.error === 'SUBSCRIPTION_CANCELLED') {
          setError('Din prenumeration har avslutats. Kontakta supporten för att återaktivera.');
          return;
        }
      }

      // Generic error fallback
      setError('Ett fel uppstod vid behandling av fakturan. Vänligen försök igen.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle form submission to create or update invoice
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all required fields
    if (!validateAllFields()) {
      setError('Vänligen fyll i alla obligatoriska fält korrekt.');
      return;
    }

    // Double-check dates are valid before formatting
    if (!isValidDate(invoiceDate)) {
      setError('Fakturadatum är ogiltigt. Vänligen välj ett giltigt datum.');
      return;
    }

    if (!isValidDate(dueDate)) {
      setError('Förfallodatum är ogiltigt. Vänligen välj ett giltigt datum.');
      return;
    }

    setError(null);
    setSuccessMessage('');
    setIsProcessing(true);

    try {
      const invoiceData = {
        supplier_id: selectedSupplier,
        invoice_number: invoiceNumber,
        invoice_date: safeFormatDate(invoiceDate, 'yyyy-MM-dd'),
        due_date: safeFormatDate(dueDate, 'yyyy-MM-dd'),
        amount: parseFloat(amount),
        status,
        payment_reference: paymentReference.trim(),
        ...(!isEditMode && ocrPayee ? { ocr_payee: ocrPayee } : {}),
      };

      let responseInvoiceId;

      if (isEditMode) {
        // Update existing invoice
        const updateInvoiceApiUrl = getApiUrl(`${companyId}/purchasing/supplier-invoices/${invoiceId}`);
        await axios.put(updateInvoiceApiUrl, invoiceData, { withCredentials: true });
        responseInvoiceId = invoiceId;

        toast.success('Fakturan har uppdaterats.');
      } else {
        // Stage file identities before invoice creation. The domain service
        // claims all selected documents in the invoice transaction.
        const createBody = { ...invoiceData };
        if (fromInboxItemIdRef.current) {
          createBody.from_inbox_item_id = fromInboxItemIdRef.current;
          createBody.expected_document_generation=inboxGenerationRef.current;
        }
        const localFiles=files.filter(file=>file!==inboxFileRef.current);
        createBody.attachments=[];
        for(const file of localFiles) {
          const data=new FormData();data.append('file',file);
          const uploaded=await axios.post(getApiUrl(`${companyId}/accounting/verifications/attachments`),data,{withCredentials:true});
          createBody.attachments.push({attachmentId:uploaded.data.attachment.attachmentId});
        }
        const createInvoiceApiUrl = getApiUrl(`${companyId}/purchasing/supplier-invoices`);
        const response = await axios.post(createInvoiceApiUrl, createBody, { withCredentials: true });
        responseInvoiceId = response.data.invoice_id;

        setSuccessMessage('Fakturan har skapats framgångsrikt!');
      }

      clearForm();
      history.push(`/${companyId}/supplier-invoices`); // Redirect to the supplier invoices list
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} invoice:`, err);
      setError(err.response?.data?.error || `Kunde inte ${isEditMode ? 'uppdatera' : 'skapa'} fakturan. Vänligen kontrollera uppgifterna och försök igen.`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Function to clear form fields
  const clearForm = () => {
    setSelectedSupplier('');
    setInvoiceNumber('');
    setInvoiceDate(new Date());
    setDueDate(new Date());
    setAmount('');
    setStatus('Pending');
    setFiles([]);
    setPreviewUrl(null);
    setError(null);
    setSuccessMessage('');
    setInitialSupplierData(null);
  };

  // Generate preview URL when files change
  useEffect(() => {
    if (files.length > 0) {
      const url = URL.createObjectURL(files[0]);
      setPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
        setPreviewUrl(null);
      };
    } else {
      setPreviewUrl(null);
    }
  }, [files]);

  // Function to remove the uploaded file
  const handleRemoveFile = () => {
    setFiles([]);
    setPreviewUrl(null);
  };

  return (
    <>
      <style>{`
        .modern-card {
          background: white;
          border: none;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          overflow: hidden;
        }

        .modern-card:hover {
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }

        .step-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          font-weight: 600;
          font-size: 0.875rem;
          transition: all 0.3s ease;
        }

        .step-indicator.completed {
          background: var(--bs-success);
          color: white;
        }

        .step-indicator.current {
          background: var(--bs-primary);
          color: white;
        }

        .step-indicator.pending {
          background: #f3f4f6;
          color: #6b7280;
        }

        .step-connector {
          flex: 1;
          height: 2px;
          background: #e5e7eb;
          margin: 0 1rem;
        }

        .step-connector.completed {
          background: var(--bs-primary);
        }

        .confidence-badge {
          display: inline-flex;
          align-items: center;
          padding: 0.25rem 0.5rem;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 500;
          margin-left: 0.5rem;
        }

        .modern-input {
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          padding: 0.75rem;
          transition: all 0.2s ease;
          font-size: 0.875rem;
        }

        .modern-input:focus {
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 3px var(--rgba-primary-1);
          outline: none;
        }

        .modern-input.error {
          border-color: #dc2626;
        }

        .field-validation {
          font-size: 0.75rem;
          margin-top: 0.25rem;
        }

        .upload-zone {
          border: 2px dashed #d1d5db;
          border-radius: 12px;
          padding: 3rem 2rem;
          text-align: center;
          transition: all 0.3s ease;
          background: #fafbfc;
        }

        .upload-zone:hover {
          border-color: var(--bs-primary);
          background: #f0f9ff;
        }

        .upload-zone.active {
          border-color: var(--bs-primary);
          background: #f0f9ff;
        }

        .supplier-suggestion {
          padding: 0.75rem;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          margin-bottom: 0.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .supplier-suggestion:hover {
          background: #f8fafc;
          border-color: var(--bs-primary);
        }

        .ocr-result-field {
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1rem;
        }

        .invoice-doc-preview .rpv-core__inner-pages {
          padding: 0 !important;
          scrollbar-gutter: auto !important;
          overflow-x: hidden !important;
        }

        .invoice-doc-preview .rpv-core__page-layer {
          margin: 0 !important;
          padding: 0 !important;
        }

        .invoice-doc-preview .rpv-core__inner-container {
          padding-right: 0 !important;
        }

        @media (max-width: 768px) {
          .step-indicator {
            width: 35px;
            height: 35px;
            font-size: 0.75rem;
          }
          
          .modern-card {
            margin-bottom: 1rem;
          }
        }
      `}</style>

      <PageHeader
        backTo={{ to: `/${companyId}/supplier-invoices`, label: 'Alla leverantörsfakturor' }}
        icon={ShoppingCart}
        title={isEditMode ? 'Redigera leverantörsfaktura' : 'Skapa leverantörsfaktura'}
      />

      <div className="col-12">
        {/* Loading State for Edit Mode */}
        {loadingInvoice && (
          <div className="modern-card mb-4">
            <div className="p-4 text-center">
              <Spinner animation="border" style={{ color: 'var(--bs-primary)' }} className="mb-3" />
              <div style={{ color: '#6b7280' }}>Laddar fakturadata...</div>
            </div>
          </div>
        )}

        {/* Modern Progress Steps */}
        {!loadingInvoice && (
          <>
            <div className="modern-card mb-4">
              <div className="p-4">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className={`step-indicator ${getStepStatus(1)}`}>
                      {getStepStatus(1) === 'completed' ? <Check size={20} /> : '1'}
                    </div>
                    <div className="ms-3">
                      <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>Ladda upp</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Dokument & OCR</div>
                    </div>
                  </div>

                  <div className={`step-connector ${currentStep > 1 ? 'completed' : ''}`}></div>

                  <div className="d-flex align-items-center">
                    <div className={`step-indicator ${getStepStatus(2)}`}>
                      {getStepStatus(2) === 'completed' ? <Check size={20} /> : '2'}
                    </div>
                    <div className="ms-3">
                      <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>Granska</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Validera uppgifter</div>
                    </div>
                  </div>

                  <div className={`step-connector ${currentStep > 2 ? 'completed' : ''}`}></div>

                  <div className="d-flex align-items-center">
                    <div className={`step-indicator ${getStepStatus(3)}`}>
                      {getStepStatus(3) === 'completed' ? <Check size={20} /> : '3'}
                    </div>
                    <div className="ms-3">
                      <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>Skapa</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Slutför faktura</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Error/Success Messages */}
            {error && (
              <Alert variant="danger" onClose={() => setError(null)} dismissible>
                <AlertCircle size={16} className="me-2" />
                {error}
                {error.includes('hittades inte') && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="ms-2"
                    onClick={() => openNewSupplierModal(initialSupplierData)}
                  >
                    <Plus size={14} className="me-1" />
                    Lägg till leverantör
                  </Button>
                )}
              </Alert>
            )}

            {successMessage && (
              <Alert variant="success">
                <CheckCircle2 size={16} className="me-2" />
                {successMessage}
              </Alert>
            )}

            {/* Duplicate-invoice warning. HIGH = same supplier + same
                invoice_number (almost certainly already in the ledger);
                MEDIUM/LOW = looser amount/date heuristics. Show the
                strongest match as the primary call-out. User can click
                through to the existing invoice or "Spara ändå" to
                dismiss the warning and continue creating a new one. */}
            {!isEditMode && duplicateCandidates.length > 0 && !duplicateAcknowledged && (() => {
              const top = duplicateCandidates[0];
              const variantByScore = { high: 'warning', medium: 'warning', low: 'info' };
              const labelByScore = {
                high:   'Liknande faktura finns redan',
                medium: 'Möjlig dubblett',
                low:    'Möjlig liknande faktura',
              };
              return (
                <Alert variant={variantByScore[top.score] || 'info'} className="mb-3">
                  <div className="d-flex align-items-start">
                    <AlertTriangle size={18} className="me-2 mt-1 flex-shrink-0" />
                    <div className="flex-grow-1">
                      <strong>{labelByScore[top.score]}</strong>
                      <div style={{ fontSize: '0.875rem', marginTop: 4 }}>
                        En faktura med nummer <strong>{top.supplier_invoice.invoice_number || '(saknas)'}</strong>{' '}
                        för <strong>{top.supplier_invoice.amount} SEK</strong> från denna leverantör
                        finns redan ({top.supplier_invoice.invoice_date}).
                        {duplicateCandidates.length > 1 && ` ${duplicateCandidates.length - 1} ytterligare möjliga matchningar.`}
                      </div>
                      <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => history.push(`/${companyId}/supplier-invoices/${top.supplier_invoice.invoice_id}`)}
                        >
                          Visa befintlig faktura
                        </Button>
                        <Button
                          size="sm"
                          variant="link"
                          onClick={() => setDuplicateAcknowledged(true)}
                        >
                          Skapa ändå
                        </Button>
                      </div>
                    </div>
                  </div>
                </Alert>
              );
            })()}

            {/* Main Content Area */}
            <Row>
              {/* Step 1: Document Upload */}
              {currentStep === 1 && (
                <Col lg={8}>
                  <div className="modern-card">
                    <div className="p-4">
                      <div className="d-flex align-items-center mb-4">
                        <Upload size={24} style={{ color: 'var(--bs-primary)' }} className="me-3" />
                        <div>
                          <h4 style={{ margin: 0, fontWeight: '600' }}>Ladda upp leverantörsfaktura</h4>
                          <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                            Släpp din faktura här för automatisk dataextraktion med AI
                          </p>
                        </div>
                      </div>

                      {isProcessing && (
                        <div className="text-center mb-4 p-4" style={{ background: '#f0f9ff', borderRadius: '8px' }}>
                          <Brain size={32} style={{ color: 'var(--bs-primary)' }} className="mb-2" />
                          <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>AI bearbetar dokument</div>
                          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                            Extraherar leverantörsuppgifter, belopp och datum...
                          </div>
                          <Spinner animation="border" style={{ color: 'var(--bs-primary)' }} className="mt-2" />
                        </div>
                      )}

                      {files.length === 0 && !isProcessing && (
                        <div className="upload-zone">
                          <Upload size={48} style={{ color: '#9ca3af' }} className="mb-3" />
                          <div style={{ fontWeight: '600', fontSize: '1.125rem', marginBottom: '0.5rem' }}>
                            Släpp din faktura här
                          </div>
                          <div style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
                            eller klicka för att välja fil
                          </div>
                          <FileDropZone
                            setFiles={setFiles}
                            onFilesUploaded={handleFilesUploaded}
                            autoUpload={true}
                            acceptedFileTypes="image/*, application/pdf"
                            mainText=""
                            subText=""
                            multiple={false}
                          />
                          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '1rem' }}>
                            Stöder PDF och bildformat (JPG, PNG) • Max 10MB
                          </div>
                        </div>
                      )}

                      {files.length > 0 && previewUrl && (
                        <div className="mt-4">
                          <div className="d-flex align-items-center justify-content-between mb-3">
                            <h5 style={{ margin: 0, fontWeight: '600' }}>
                              <Eye size={20} className="me-2" />
                              Förhandsgranskning
                            </h5>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => setCurrentStep(2)}
                              >
                                Fyll i manuellt
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={handleRemoveFile}
                              >
                                <X size={16} className="me-1" />
                                Ta bort
                              </Button>
                            </div>
                          </div>

                          <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                            {files[0].type.startsWith('image/') && (
                              <img
                                src={previewUrl}
                                alt="Preview"
                                style={{ width: '100%', maxHeight: '400px', objectFit: 'contain' }}
                              />
                            )}
                            {files[0].type === 'application/pdf' && (
                              <div style={{ height: '500px' }}>
                                <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js">
                                  <Viewer fileUrl={previewUrl} defaultScale={SpecialZoomLevel.PageWidth} />
                                </Worker>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              )}
              {/* Info Panel - Shows on step 1 */}
              {currentStep === 1 && (
                <Col lg={4}>
                  <div className="modern-card">
                    <div className="p-4">
                      <div className="d-flex align-items-center mb-3">
                        <Info size={20} style={{ color: '#3b82f6' }} className="me-2" />
                        <h5 style={{ margin: 0, fontWeight: '600' }}>Så fungerar det</h5>
                      </div>

                      <div className="mb-4">
                        <div className="d-flex align-items-start mb-3">
                          <Upload size={16} style={{ color: 'var(--bs-primary)' }} className="me-2 mt-1" />
                          <div>
                            <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>1. Ladda upp dokument</div>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                              Släpp PDF eller bildfil med din leverantörsfaktura
                            </div>
                          </div>
                        </div>

                        <div className="d-flex align-items-start mb-3">
                          <Brain size={16} style={{ color: 'var(--bs-primary)' }} className="me-2 mt-1" />
                          <div>
                            <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>2. AI-analys</div>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                              Automatisk extraktion av leverantör, belopp och datum
                            </div>
                          </div>
                        </div>

                        <div className="d-flex align-items-start mb-3">
                          <Eye size={16} style={{ color: 'var(--bs-primary)' }} className="me-2 mt-1" />
                          <div>
                            <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>3. Granska & korrigera</div>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                              Kontrollera AI-resultatet och justera vid behov
                            </div>
                          </div>
                        </div>

                        <div className="d-flex align-items-start">
                          <CheckCircle2 size={16} style={{ color: 'var(--bs-primary)' }} className="me-2 mt-1" />
                          <div>
                            <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>4. Skapa faktura</div>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                              Slutför och spara i systemet för betalningshantering
                            </div>
                          </div>
                        </div>
                      </div>

                      <div style={{ background: '#f0f9ff', borderRadius: '8px', padding: '1rem' }}>
                        <div className="d-flex align-items-center mb-2">
                          <AlertTriangle size={16} style={{ color: '#0284c7' }} className="me-2" />
                          <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>Tips</div>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#0c4a6e' }}>
                          För bästa resultat, se till att dokumentet är tydligt och alla viktiga uppgifter syns väl.
                        </div>
                      </div>

                      {/* Skip scanning option */}
                      <div className="mt-4 pt-3" style={{ borderTop: '1px solid #e5e7eb' }}>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                          Har du ingen fil att ladda upp?
                        </div>
                        <Button
                          variant="link"
                          className="p-0 d-flex align-items-center"
                          style={{ fontSize: '0.875rem', color: 'var(--bs-primary)', textDecoration: 'none' }}
                          onClick={() => {
                            setFiles([]);
                            setPreviewUrl(null);
                            setOcrData(null);
                            setCurrentStep(2);
                          }}
                        >
                          <Edit3 size={14} className="me-2" />
                          Ange uppgifter manuellt
                          <ArrowRight size={14} className="ms-1" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Col>
              )}

              {/* Step 2: Review/Edit Invoice Details */}
              {currentStep === 2 && (
                <>
                  <Col lg={6}>
                    <div className="modern-card">
                      <div className="p-4">
                        <div className="d-flex align-items-center mb-4">
                          {isProcessing ? (
                            <Spinner animation="border" style={{ color: 'var(--bs-primary)' }} className="me-3" />
                          ) : isEditMode ? (
                            <Edit3 size={24} style={{ color: 'var(--bs-primary)' }} className="me-3" />
                          ) : ocrData ? (
                            <Brain size={24} style={{ color: 'var(--bs-primary)' }} className="me-3" />
                          ) : (
                            <FileText size={24} style={{ color: 'var(--bs-primary)' }} className="me-3" />
                          )}
                          <div>
                            {/* While the invoice is being scanned (notably the
                                from-inbox flow, where the user lands here with
                                the scan already running in the background) the
                                header explicitly says so — otherwise the empty
                                fields read as "fill this in yourself" and the
                                auto-fill a few seconds later looks like a glitch. */}
                            <h4 style={{ margin: 0, fontWeight: '600' }}>
                              {isProcessing ? 'Läser av fakturan…' : isEditMode ? 'Redigera faktura' : ocrData ? 'AI-extraherade uppgifter' : 'Ange fakturauppgifter'}
                            </h4>
                            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                              {isProcessing ? 'Det här tar några sekunder — fälten fylls i automatiskt.' : isEditMode ? 'Uppdatera fakturauppgifterna' : ocrData ? 'Granska och korrigera vid behov' : 'Fyll i uppgifterna manuellt'}
                            </p>
                          </div>
                        </div>

                        {/* Invoice Fields - Always shown */}
                        <div className="mb-4">
                          {/* Invoice Number */}
                          <div className="ocr-result-field">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <label style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                                <FileText size={16} className="me-2" />
                                Fakturanummer *
                              </label>
                              {ocrData?.invoiceId && (
                                <div
                                  className="confidence-badge"
                                  style={{
                                    background: `${getConfidenceColor(ocrData.invoiceId.confidence)}15`,
                                    color: getConfidenceColor(ocrData.invoiceId.confidence)
                                  }}
                                >
                                  {(ocrData.invoiceId.confidence * 100).toFixed(0)}% säker
                                </div>
                              )}
                            </div>
                            <input
                              type="text"
                              className={`modern-input w-100 ${fieldValidation.invoiceNumber && !fieldValidation.invoiceNumber.isValid ? 'error' : ''}`}
                              value={invoiceNumber}
                              onChange={(e) => {
                                setInvoiceNumber(e.target.value);
                                validateField('invoiceNumber', e.target.value);
                              }}
                              placeholder="Ange fakturanummer"
                            />
                            {fieldValidation.invoiceNumber && !fieldValidation.invoiceNumber.isValid && (
                              <div className="field-validation" style={{ color: '#dc2626' }}>
                                {fieldValidation.invoiceNumber.message}
                              </div>
                            )}
                          </div>

                          {/* Betalningsreferens (OCR) — quoted on the payment file row (ISO 20022 session 2) */}
                          <div className="ocr-result-field">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <label style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                                <Hash size={16} className="me-2" />
                                Betalningsreferens (OCR)
                              </label>
                              {ocrData?.extended?.ocrNumber?.value && (
                                <div className="confidence-badge" style={{ background: `${getConfidenceColor(ocrData.extended.ocrNumber.confidence)}15`, color: getConfidenceColor(ocrData.extended.ocrNumber.confidence) }}>
                                  {(ocrData.extended.ocrNumber.confidence * 100).toFixed(0)}% säker
                                </div>
                              )}
                            </div>
                            <input
                              type="text"
                              className="modern-input w-100"
                              value={paymentReference}
                              onChange={(e) => setPaymentReference(e.target.value)}
                              placeholder="OCR-nummer eller annan referens att ange vid betalning"
                              maxLength={50}
                            />
                            <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: 4 }}>
                              Referensen leverantören vill ha på betalningen. Ett giltigt OCR-nummer sparas som OCR, annan text som meddelande.
                            </div>
                          </div>

                          {/* Amount */}
                          <div className="ocr-result-field">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <label style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                                <DollarSign size={16} className="me-2" />
                                Belopp (inkl. moms) *
                              </label>
                              {ocrData?.invoiceTotal && (
                                <div
                                  className="confidence-badge"
                                  style={{
                                    background: `${getConfidenceColor(ocrData.invoiceTotal.confidence)}15`,
                                    color: getConfidenceColor(ocrData.invoiceTotal.confidence)
                                  }}
                                >
                                  {(ocrData.invoiceTotal.confidence * 100).toFixed(0)}% säker
                                </div>
                              )}
                            </div>
                            <input
                              type="number"
                              className={`modern-input w-100 ${fieldValidation.amount && !fieldValidation.amount.isValid ? 'error' : ''}`}
                              value={amount}
                              onChange={(e) => {
                                setAmount(e.target.value);
                                validateField('amount', e.target.value);
                              }}
                              min="0"
                              step="0.01"
                              placeholder="Ange belopp"
                            />
                            {fieldValidation.amount && !fieldValidation.amount.isValid && (
                              <div className="field-validation" style={{ color: '#dc2626' }}>
                                {fieldValidation.amount.message}
                              </div>
                            )}
                          </div>

                          {/* Dates */}
                          <Row>
                            <Col md={6}>
                              <div className="ocr-result-field">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                  <label style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                                    <Calendar size={16} className="me-2" />
                                    Fakturadatum *
                                  </label>
                                  {ocrData?.invoiceDate && (
                                    <div
                                      className="confidence-badge"
                                      style={{
                                        background: `${getConfidenceColor(ocrData.invoiceDate.confidence)}15`,
                                        color: getConfidenceColor(ocrData.invoiceDate.confidence)
                                      }}
                                    >
                                      {(ocrData.invoiceDate.confidence * 100).toFixed(0)}% säker
                                    </div>
                                  )}
                                </div>
                                <DatePicker
                                  className={`modern-input w-100 ${fieldValidation.invoiceDate && !fieldValidation.invoiceDate.isValid ? 'error' : ''}`}
                                  selected={isValidDate(invoiceDate) ? invoiceDate : null}
                                  onChange={(date) => {
                                    setInvoiceDate(date);
                                    validateField('invoiceDate', date);
                                  }}
                                  dateFormat="yyyy-MM-dd"
                                  placeholderText="Välj datum"
                                  maxDate={new Date()}
                                />
                                {fieldValidation.invoiceDate && !fieldValidation.invoiceDate.isValid && (
                                  <div className="field-validation" style={{ color: '#dc2626' }}>
                                    {fieldValidation.invoiceDate.message}
                                  </div>
                                )}
                                {fiscalYearWarning && (
                                  <Alert variant="warning" className="mt-2 mb-0 py-2 px-3" style={{ fontSize: '0.8rem' }}>
                                    <AlertTriangle size={14} className="me-1" />
                                    {fiscalYearWarning}
                                  </Alert>
                                )}
                              </div>
                            </Col>
                            <Col md={6}>
                              <div className="ocr-result-field">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                  <label style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                                    <Calendar size={16} className="me-2" />
                                    Förfallodatum *
                                  </label>
                                  {ocrData?.dueDate && (
                                    <div
                                      className="confidence-badge"
                                      style={{
                                        background: `${getConfidenceColor(ocrData.dueDate.confidence)}15`,
                                        color: getConfidenceColor(ocrData.dueDate.confidence)
                                      }}
                                    >
                                      {(ocrData.dueDate.confidence * 100).toFixed(0)}% säker
                                    </div>
                                  )}
                                </div>
                                <DatePicker
                                  className={`modern-input w-100 ${fieldValidation.dueDate && !fieldValidation.dueDate.isValid ? 'error' : ''}`}
                                  selected={isValidDate(dueDate) ? dueDate : null}
                                  onChange={(date) => {
                                    setDueDate(date);
                                    validateField('dueDate', date);
                                  }}
                                  dateFormat="yyyy-MM-dd"
                                  placeholderText="Välj datum"
                                  minDate={isValidDate(invoiceDate) ? invoiceDate : undefined}
                                />
                                {fieldValidation.dueDate && !fieldValidation.dueDate.isValid && (
                                  <div className="field-validation" style={{ color: '#dc2626' }}>
                                    {fieldValidation.dueDate.message}
                                  </div>
                                )}
                              </div>
                            </Col>
                          </Row>
                        </div>

                        {/* Supplier Selection */}
                        <div className="ocr-result-field">
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <label style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                              <Building size={16} className="me-2" />
                              Leverantör *
                            </label>
                            {ocrData?.vendorName && (
                              <div
                                className="confidence-badge"
                                style={{
                                  background: `${getConfidenceColor(ocrData.vendorName.confidence)}15`,
                                  color: getConfidenceColor(ocrData.vendorName.confidence)
                                }}
                              >
                                {(ocrData.vendorName.confidence * 100).toFixed(0)}% säker
                              </div>
                            )}
                          </div>

                          {/* Show extracted vendor name when no supplier is matched */}
                          {!selectedSupplier && ocrData?.vendorName?.value && (
                            <div style={{
                              background: '#fef3c7',
                              border: '1px solid #f59e0b',
                              borderRadius: '8px',
                              padding: '0.75rem',
                              marginBottom: '1rem'
                            }}>
                              <div className="d-flex align-items-center justify-content-between">
                                <div>
                                  <div className="d-flex align-items-center mb-1">
                                    <AlertTriangle size={14} style={{ color: '#d97706' }} className="me-2" />
                                    <span style={{ fontSize: '0.75rem', color: '#92400e', fontWeight: '500' }}>
                                      Extraherad leverantör ej i systemet
                                    </span>
                                  </div>
                                  <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#78350f' }}>
                                    {ocrData.vendorName.value}
                                  </div>
                                  {ocrData.extended?.sellerOrganizationNumber?.value && (
                                    <div style={{ fontSize: '0.75rem', color: '#92400e' }}>
                                      Org.nr: {ocrData.extended.sellerOrganizationNumber.value}
                                    </div>
                                  )}
                                </div>
                                <Button
                                  variant="warning"
                                  size="sm"
                                  onClick={() => openNewSupplierModal(initialSupplierData)}
                                  style={{ fontWeight: '500', fontSize: '0.75rem' }}
                                >
                                  <Plus size={14} className="me-1" />
                                  Lägg till
                                </Button>
                              </div>
                            </div>
                          )}

                          {/* Selected Supplier Confirmation */}
                          {selectedSupplier && suppliers.find(s => s.supplier_id === selectedSupplier) && (
                            <div style={{
                              background: '#ecfdf5',
                              border: '1px solid var(--bs-success)',
                              borderRadius: '8px',
                              padding: '0.75rem',
                              marginBottom: '1rem'
                            }}>
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                  <CheckCircle2 size={16} style={{ color: 'var(--bs-success)' }} className="me-2" />
                                  <div>
                                    <div style={{ fontWeight: '500', fontSize: '0.875rem', color: 'var(--bs-success)' }}>
                                      ✓ {suppliers.find(s => s.supplier_id === selectedSupplier)?.name}
                                    </div>
                                    {suppliers.find(s => s.supplier_id === selectedSupplier)?.orgnumber && (
                                      <div style={{ fontSize: '0.75rem', color: '#065f46' }}>
                                        Org.nr: {suppliers.find(s => s.supplier_id === selectedSupplier)?.orgnumber}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <Button
                                  variant="link"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedSupplier('');
                                    setSupplierSuggestions([]);
                                  }}
                                  style={{ color: 'var(--bs-success)', textDecoration: 'none' }}
                                >
                                  Ändra
                                </Button>
                              </div>
                            </div>
                          )}

                          {/* Supplier Suggestions - Only show if no supplier selected */}
                          {!selectedSupplier && supplierSuggestions.length > 0 && (
                            <div className="mb-3">
                              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                                Föreslagna leverantörer:
                              </div>
                              {supplierSuggestions.map(supplier => (
                                <div
                                  key={supplier.supplier_id}
                                  className="supplier-suggestion"
                                  onClick={() => {
                                    setSelectedSupplier(supplier.supplier_id);
                                    setSupplierSuggestions([]);
                                  }}
                                >
                                  <div style={{ fontWeight: '500' }}>{supplier.name}</div>
                                  {supplier.orgnumber && (
                                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                      Org.nr: {supplier.orgnumber}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Dropdown - Only show if no supplier selected */}
                          {!selectedSupplier && (
                            <>
                              <div className="d-flex gap-2">
                                <select
                                  className="modern-input flex-grow-1"
                                  value={selectedSupplier}
                                  onChange={(e) => {
                                    setSelectedSupplier(e.target.value);
                                  }}
                                >
                                  <option value="">Välj leverantör</option>
                                  {suppliers.map((supplier) => (
                                    <option key={supplier.supplier_id} value={supplier.supplier_id}>
                                      {supplier.name}
                                    </option>
                                  ))}
                                </select>
                                <Button
                                  variant="outline-primary"
                                  onClick={() => openNewSupplierModal(initialSupplierData)}
                                  style={{ borderColor: 'var(--bs-primary)', color: 'var(--bs-primary)' }}
                                >
                                  <Plus size={16} />
                                </Button>
                              </div>

                              {fieldValidation.supplier && !fieldValidation.supplier.isValid && (
                                <div className="field-validation" style={{ color: '#dc2626' }}>
                                  {fieldValidation.supplier.message}
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="d-flex justify-content-between mt-4">
                          {isEditMode ? (
                            <Button
                              variant="outline-secondary"
                              onClick={() => history.goBack()}
                            >
                              <ArrowLeft size={16} className="me-2" />
                              Avbryt
                            </Button>
                          ) : (
                            <Button
                              variant="outline-secondary"
                              onClick={() => {
                                // Reset all form state so a new scan starts fresh
                                setSelectedSupplier('');
                                setInvoiceNumber('');
                                setInvoiceDate(new Date());
                                setDueDate(new Date());
                                setAmount('');
                                setOcrData(null);
                                setFiles([]);
                                setPreviewUrl(null);
                                setFieldValidation({});
                                setSupplierSuggestions([]);
                                setInitialSupplierData(null);
                                setError(null);
                                setCurrentStep(1);
                              }}
                            >
                              <ArrowLeft size={16} className="me-2" />
                              Tillbaka
                            </Button>
                          )}
                          <Button
                            variant="primary"
                            onClick={() => {
                              if (validateAllFields()) {
                                setCurrentStep(3);
                              }
                            }}
                            disabled={!canProceedToStep3()}
                            style={{ background: 'var(--bs-primary)', borderColor: 'var(--bs-primary)' }}
                          >
                            Fortsätt
                            <ArrowRight size={16} className="ms-2" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Col>

                  {/* Document Preview */}
                  <Col lg={6} style={{ paddingLeft: 0, paddingRight: 0 }}>
                    <div style={{ position: 'sticky', top: '0' }}>
                      <div className="d-flex align-items-center mb-2" style={{ paddingLeft: '0.5rem' }}>
                        <Eye size={18} style={{ color: '#6b7280' }} className="me-2" />
                        <span style={{ fontWeight: '600', fontSize: '0.875rem', color: '#6b7280' }}>
                          Förhandsgranskning
                        </span>
                      </div>
                      {/* New file preview (for create mode or if new file uploaded in edit mode) */}
                      {files.length > 0 && previewUrl && (
                        <div className="invoice-doc-preview" style={{ overflow: 'hidden', background: '#f8f9fa' }}>
                          {files[0].type.startsWith('image/') && (
                            <img
                              src={previewUrl}
                              alt="Preview"
                              style={{ width: '100%', display: 'block' }}
                            />
                          )}
                          {files[0].type === 'application/pdf' && (
                            <div style={{ height: 'calc(100vh - 140px)', minHeight: '600px' }}>
                              <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js">
                                <Viewer fileUrl={previewUrl} defaultScale={SpecialZoomLevel.PageWidth} />
                              </Worker>
                            </div>
                          )}
                        </div>
                      )}

                        {/* Existing attachments (for edit mode) */}
                        {isEditMode && files.length === 0 && (
                          <>
                            {existingAttachments.length > 0 ? (
                              <div>
                                {existingAttachments.map((attachment, index) => {
                                  const fileName = attachment.file_name || attachment.filename;
                                  const isImage = attachment.file_type?.startsWith('image/') || fileName?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                                  const isPdf = attachment.file_type === 'application/pdf' || fileName?.endsWith('.pdf');
                                  const blobUrl = attachmentUrls[attachment.attachment_id];

                                  return (
                                    <div key={attachment.attachment_id || index} className="mb-3">
                                      {/* Loading state */}
                                      {!blobUrl && (
                                        <div className="text-center p-4" style={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                                          <Spinner animation="border" size="sm" style={{ color: 'var(--bs-primary)' }} />
                                          <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                                            Laddar {fileName || 'fil'}...
                                          </div>
                                        </div>
                                      )}
                                      {/* Image display */}
                                      {blobUrl && isImage && (
                                        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                                          <img
                                            src={blobUrl}
                                            alt={fileName || 'Attachment'}
                                            style={{ width: '100%', maxHeight: '600px', objectFit: 'contain' }}
                                          />
                                        </div>
                                      )}
                                      {/* PDF display */}
                                      {blobUrl && isPdf && (
                                        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', height: '600px' }}>
                                          <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js">
                                            <Viewer fileUrl={blobUrl} defaultScale={SpecialZoomLevel.PageWidth} />
                                          </Worker>
                                        </div>
                                      )}
                                      {/* Other file types */}
                                      {blobUrl && !isImage && !isPdf && (
                                        <div
                                          style={{
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            padding: '1rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                          }}
                                        >
                                          <div className="d-flex align-items-center">
                                            <FileText size={24} className="me-3" style={{ color: '#6b7280' }} />
                                            <div>
                                              <div style={{ fontWeight: '500' }}>{fileName || 'Dokument'}</div>
                                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                {attachment.file_type || 'Okänd typ'}
                                              </div>
                                            </div>
                                          </div>
                                          <a
                                            href={blobUrl}
                                            download={fileName}
                                            className="btn btn-outline-primary btn-sm"
                                          >
                                            <Eye size={14} className="me-1" />
                                            Ladda ner
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div
                                style={{
                                  background: '#f8fafc',
                                  borderRadius: '8px',
                                  padding: '2rem',
                                  textAlign: 'center'
                                }}
                              >
                                <FileText size={32} style={{ color: '#9ca3af' }} className="mb-2" />
                                <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                  Inga bifogade dokument
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {/* Upload zone for create mode without file - allows AI scanning */}
                        {!isEditMode && files.length === 0 && (
                          <>
                            {isProcessing ? (
                              <div
                                style={{
                                  background: '#f0f9ff',
                                  borderRadius: '8px',
                                  padding: '3rem 2rem',
                                  textAlign: 'center'
                                }}
                              >
                                <Brain size={40} style={{ color: 'var(--bs-primary)' }} className="mb-3" />
                                <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>AI bearbetar dokument</div>
                                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                                  Extraherar leverantörsuppgifter, belopp och datum...
                                </div>
                                <Spinner animation="border" style={{ color: 'var(--bs-primary)' }} />
                              </div>
                            ) : (
                              <div
                                className="upload-zone"
                                style={{
                                  background: '#f8fafc',
                                  borderRadius: '8px',
                                  padding: '2rem',
                                  textAlign: 'center',
                                  border: '2px dashed #d1d5db',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <Upload size={40} style={{ color: '#9ca3af' }} className="mb-3" />
                                <div style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '0.5rem', color: '#374151' }}>
                                  Ladda upp fakturafil
                                </div>
                                <div style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
                                  Släpp fil här eller klicka för att välja
                                </div>
                                <div className="d-flex align-items-center justify-content-center gap-2 mb-3">
                                  <Brain size={16} style={{ color: 'var(--bs-primary)' }} />
                                  <span style={{ fontSize: '0.875rem', color: 'var(--bs-primary)', fontWeight: '500' }}>
                                    AI extraherar data automatiskt
                                  </span>
                                </div>
                                <FileDropZone
                                  setFiles={setFiles}
                                  onFilesUploaded={handleFilesUploaded}
                                  autoUpload={true}
                                  acceptedFileTypes="image/*, application/pdf"
                                  mainText=""
                                  subText=""
                                  multiple={false}
                                />
                                <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '1rem' }}>
                                  Stöder PDF och bildformat (JPG, PNG) • Max 10MB
                                </div>
                              </div>
                            )}
                          </>
                        )}
                    </div>
                  </Col>
                </>
              )}

              {/* Step 3: Final Review & Submit */}
              {currentStep === 3 && (
                <>
                  <Col lg={7}>
                    <div className="modern-card">
                      <div className="p-4">
                        <div className="d-flex align-items-center mb-4">
                          <div
                            style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '12px',
                              background: 'var(--primary)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginRight: '1rem'
                            }}
                          >
                            <CheckCircle2 size={24} style={{ color: 'white' }} />
                          </div>
                          <div>
                            <h4 style={{ margin: 0, fontWeight: '600' }}>
                              {isEditMode ? 'Uppdatera leverantörsfaktura' : 'Granska & slutför'}
                            </h4>
                            <p style={{ margin: 0, color: 'var(--secondary)', fontSize: '0.875rem' }}>
                              {isEditMode
                                ? 'Kontrollera uppgifterna innan du sparar'
                                : 'Kontrollera uppgifterna innan du skapar fakturan'}
                            </p>
                          </div>
                        </div>

                        <form onSubmit={handleSubmit}>
                          {/* Invoice Summary Cards */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                            {/* Supplier Card */}
                            <div
                              style={{
                                background: 'var(--rgba-primary-1)',
                                borderRadius: '12px',
                                padding: '1rem',
                                border: '1px solid var(--rgba-primary-2)'
                              }}
                            >
                              <div className="d-flex align-items-center mb-2">
                                <Building size={16} style={{ color: 'var(--secondary)' }} className="me-2" />
                                <span style={{ fontSize: '0.75rem', color: 'var(--secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                  Leverantör
                                </span>
                              </div>
                              <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>
                                {suppliers.find(s => s.supplier_id === selectedSupplier)?.name || 'Inte vald'}
                              </div>
                            </div>

                            {/* Invoice Number Card */}
                            <div
                              style={{
                                background: 'var(--rgba-primary-1)',
                                borderRadius: '12px',
                                padding: '1rem',
                                border: '1px solid var(--rgba-primary-2)'
                              }}
                            >
                              <div className="d-flex align-items-center mb-2">
                                <FileText size={16} style={{ color: 'var(--secondary)' }} className="me-2" />
                                <span style={{ fontSize: '0.75rem', color: 'var(--secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                  Fakturanummer
                                </span>
                              </div>
                              <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>
                                {invoiceNumber}
                                {paymentReference.trim() && (
                                  <div style={{ fontWeight: '500', fontSize: '0.8125rem', color: 'var(--secondary)', marginTop: 2 }}>
                                    Betalningsreferens: <span style={{ fontVariantNumeric: 'tabular-nums' }}>{paymentReference.trim()}</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Amount Card */}
                            <div
                              style={{
                                background: 'var(--rgba-primary-2)',
                                borderRadius: '12px',
                                padding: '1rem',
                                border: '1px solid var(--rgba-primary-3)'
                              }}
                            >
                              <div className="d-flex align-items-center mb-2">
                                <DollarSign size={16} style={{ color: 'var(--primary)' }} className="me-2" />
                                <span style={{ fontSize: '0.75rem', color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                  Belopp
                                </span>
                              </div>
                              <div style={{ fontWeight: '700', fontSize: '1.25rem', color: 'var(--primary-dark)' }}>
                                {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(parseFloat(amount) || 0)}
                              </div>
                            </div>

                            {/* Invoice Date Card */}
                            <div
                              style={{
                                background: 'var(--rgba-primary-1)',
                                borderRadius: '12px',
                                padding: '1rem',
                                border: '1px solid var(--rgba-primary-2)'
                              }}
                            >
                              <div className="d-flex align-items-center mb-2">
                                <Calendar size={16} style={{ color: 'var(--secondary)' }} className="me-2" />
                                <span style={{ fontSize: '0.75rem', color: 'var(--secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                  Fakturadatum
                                </span>
                              </div>
                              <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>
                                {isValidDate(invoiceDate) ? safeFormatDate(invoiceDate, 'yyyy-MM-dd') : (
                                  <span style={{ color: 'var(--bs-danger)' }}>Datum saknas</span>
                                )}
                              </div>
                            </div>

                            {/* Due Date Card */}
                            <div
                              style={{
                                background: 'rgba(255, 170, 43, 0.1)',
                                borderRadius: '12px',
                                padding: '1rem',
                                border: '1px solid rgba(255, 170, 43, 0.3)',
                                gridColumn: 'span 2'
                              }}
                            >
                              <div className="d-flex align-items-center justify-content-between">
                                <div>
                                  <div className="d-flex align-items-center mb-2">
                                    <AlertTriangle size={16} style={{ color: 'var(--bs-warning)' }} className="me-2" />
                                    <span style={{ fontSize: '0.75rem', color: 'var(--bs-warning)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                      Förfallodatum
                                    </span>
                                  </div>
                                  <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>
                                    {isValidDate(dueDate) ? safeFormatDate(dueDate, 'yyyy-MM-dd') : (
                                      <span style={{ color: 'var(--bs-danger)' }}>Datum saknas</span>
                                    )}
                                  </div>
                                </div>
                                {isValidDate(dueDate) && (
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--secondary)' }}>
                                      {(() => {
                                        const today = new Date();
                                        const due = new Date(dueDate);
                                        const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
                                        if (diffDays < 0) return `${Math.abs(diffDays)} dagar försenad`;
                                        if (diffDays === 0) return 'Förfaller idag';
                                        return `${diffDays} dagar kvar`;
                                      })()}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Attachments Summary */}
                          {(files.length > 0 || existingAttachments.length > 0) && (
                            <div
                              style={{
                                background: 'var(--rgba-primary-1)',
                                borderRadius: '12px',
                                padding: '1rem',
                                border: '1px solid var(--rgba-primary-2)',
                                marginBottom: '1.5rem'
                              }}
                            >
                              <div className="d-flex align-items-center">
                                <Eye size={18} style={{ color: 'var(--primary)' }} className="me-2" />
                                <span style={{ fontWeight: '500', color: 'var(--primary)' }}>
                                  {files.length > 0
                                    ? `${files.length} ${files.length === 1 ? 'dokument' : 'dokument'} bifogat`
                                    : `${existingAttachments.length} ${existingAttachments.length === 1 ? 'dokument' : 'dokument'} bifogat`}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="d-flex justify-content-between mt-4">
                            <Button
                              variant="outline-secondary"
                              onClick={() => setCurrentStep(2)}
                            >
                              <ArrowLeft size={16} className="me-2" />
                              Tillbaka
                            </Button>
                            <Button
                              variant="primary"
                              type="submit"
                              disabled={isProcessing}
                              style={{ background: 'var(--bs-primary)', borderColor: 'var(--bs-primary)' }}
                            >
                              {isProcessing ? (
                                <>
                                  <Loader2 size={16} className="me-2 spin" />
                                  {isEditMode ? 'Uppdaterar...' : 'Skapar...'}
                                </>
                              ) : (
                                <>
                                  <CheckCircle2 size={16} className="me-2" />
                                  {isEditMode ? 'Spara ändringar' : 'Skapa faktura'}
                                </>
                              )}
                            </Button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </Col>

                  {/* Document Preview in Step 3 */}
                  <Col lg={5}>
                    <div className="modern-card" style={{ overflow: 'hidden' }}>
                      {/* New file preview */}
                      {files.length > 0 && previewUrl && (
                        <div className="invoice-doc-preview">
                          {files[0].type.startsWith('image/') && (
                            <img
                              src={previewUrl}
                              alt="Preview"
                              style={{ width: '100%', display: 'block' }}
                            />
                          )}
                          {files[0].type === 'application/pdf' && (
                            <div style={{ height: '500px' }}>
                              <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js">
                                <Viewer fileUrl={previewUrl} defaultScale={SpecialZoomLevel.PageWidth} />
                              </Worker>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Existing attachment preview */}
                      {isEditMode && files.length === 0 && existingAttachments.length > 0 && (
                        <div className="invoice-doc-preview">
                          {existingAttachments.slice(0, 1).map((attachment) => {
                            const blobUrl = attachmentUrls[attachment.attachment_id];
                            const fileName = attachment.file_name || attachment.filename;
                            const isImage = attachment.file_type?.startsWith('image/');
                            const isPdf = attachment.file_type === 'application/pdf';

                            if (!blobUrl) {
                              return (
                                <div key={attachment.attachment_id} className="text-center p-4">
                                  <Spinner animation="border" size="sm" style={{ color: 'var(--primary)' }} />
                                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                                    Laddar...
                                  </div>
                                </div>
                              );
                            }

                            return (
                              <div key={attachment.attachment_id}>
                                {isImage && (
                                  <img
                                    src={blobUrl}
                                    alt={fileName}
                                    style={{ width: '100%', display: 'block' }}
                                  />
                                )}
                                {isPdf && (
                                  <div style={{ height: '500px' }}>
                                    <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js">
                                      <Viewer fileUrl={blobUrl} defaultScale={SpecialZoomLevel.PageWidth} />
                                    </Worker>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Empty state */}
                      {files.length === 0 && existingAttachments.length === 0 && (
                        <div style={{ padding: '2rem', textAlign: 'center' }}>
                          <FileText size={32} style={{ color: '#9ca3af' }} className="mb-2" />
                          <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                            Inget dokument
                          </div>
                        </div>
                      )}
                    </div>
                  </Col>
                </>
              )}
            </Row>
          </>
        )}

        {/* Supplier Modal */}
        <NewSupplierModal
          key={modalKey}
          show={showNewSupplierModal}
          handleClose={handleClose}
          handleSave={handleSave}
          handleUpdate={handleSave}
          initialSupplierData={initialSupplierData}
        />
      </div>
    </>
  );
};

export default CreateNewSupplierInvoice;
